'use client';

import React, { useMemo, useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { toast } from 'react-toastify';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import useFetchVendors from '../../hooks/useFetchVendors';
import ManageVendor from '../../components/VendorMgmt/ManageVendor';
import useAddVendor from '../../hooks/useAddVendor';
import { Tooltip, IconButton } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import { ModuleRegistry } from 'ag-grid-community';
import {
  ClientSideRowModelModule,
  ClipboardModule,
  ExcelExportModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowStyleModule,
  RowSelectionModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
} from 'ag-grid-enterprise';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ClipboardModule,
  ExcelExportModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowStyleModule,
  RowSelectionModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
]);

const VendorMgmt = () => {
  const { fetchVendorData, loadingFetchVendorData, errorFetchVendorData } = useFetchVendors();
  const { addVendor, loadingAddVendor, errorAddVendor } = useAddVendor();
  const [loadingGrid, setLoadingGrid] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [newVendor, setNewVendor] = useState({ vendorName: '', confirmVendorName: '' });
  const [open, setOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const handleViewCostClick = (params) => {
    setSelectedVendor(params.data);
  };

  const columnDefs = useMemo(
    () => [
      {
        field: 'actions',
        headerName: 'Manage',
        width: 130,
        cellRenderer: (params) => (
          <Tooltip title="Manage Vendor" arrow>
            <IconButton color="primary" size="small" onClick={() => handleViewCostClick(params)}>
              <ManageAccountsIcon />
            </IconButton>
          </Tooltip>
        ),
        cellStyle: { textAlign: 'center' },
      },
      { headerName: 'NetSuite Vendor ID', field: 'nsVendorId', filter: true, sortable: true },
      { headerName: 'VeriForce ID', field: 'nsVeriForceId', filter: true, sortable: true },
      { headerName: 'Pirate Vendor ID', field: 'vendorId', filter: true, sortable: true, hide: true },
      { headerName: 'Company Name', field: 'companyName', filter: true, sortable: true },
      { headerName: 'Compliance Status', field: 'complianceStatus', filter: true, sortable: true },
      { headerName: 'Vendor Type', field: 'vendorType', filter: true, sortable: true },
      { headerName: 'Notification Email', field: 'notificationEmail', filter: true, sortable: true },
      { headerName: 'Products & Services', field: 'productsServices', filter: true, sortable: true },
      { headerName: 'SSQ Score', field: 'ssqScore', filter: true, sortable: true },
      { headerName: 'SSQ Description', field: 'ssqScoreDescription', filter: true, sortable: true },
      { headerName: 'Calculated Score', field: 'calculatedScore', filter: true, sortable: true },
      { headerName: 'Insurance', field: 'insurance', filter: true, sortable: true },
      { headerName: 'OSHA Verified', field: 'oshaVerified', filter: true, sortable: true },
      { headerName: 'EMR Range', field: 'emrRange', filter: true, sortable: true },
      { headerName: 'EMR Verified', field: 'emrVerified', filter: true, sortable: true },
      { headerName: 'TRIR Range', field: 'trirRange', filter: true, sortable: true },
      { headerName: 'Background Checks', field: 'backgroundChecks', filter: true, sortable: true },
      { headerName: 'Conducts Safety Meetings', field: 'conductSafetyMeetings', filter: true, sortable: true },
      { headerName: 'Drug & Alcohol Program', field: 'drugAlcholProgram', filter: true, sortable: true },
      { headerName: 'Employee Skills Knowledge', field: 'employeeSkillsKnowledge', filter: true, sortable: true },
      { headerName: 'HSE Manual Uploaded', field: 'hseManualUploaded', filter: true, sortable: true },
      { headerName: 'Job Safety Analysis', field: 'jobSafetyAnalysis', filter: true, sortable: true },
      { headerName: 'PPE', field: 'personalProtectiveEquipment', filter: true, sortable: true },
      { headerName: 'Short Service Employee', field: 'shortServiceEmployee', filter: true, sortable: true },
      { headerName: 'Training Program Uploaded', field: 'trainingProgramUploaded', filter: true, sortable: true },
      { headerName: 'Written Motor Vehicle Safety', field: 'writtenMotorVehicleSaftey', filter: true, sortable: true },
      { headerName: 'SSQ Color Code', field: 'ssqColorCode', filter: true, sortable: true },
    ],
    []
  );

  const getRowStyle = (params) => {
    if (!params.data?.ssqScore || isNaN(Number(params.data.ssqScore))) {
      return {};
    }
    return { backgroundColor: params.data.ssqColorCode };
  };

  const videoUrl = 'https://wwmstaticwebdeploy.blob.core.windows.net/tutorials/adminTest.mp4';

  useEffect(() => {
    const getVendors = async () => {
      try {
        const data = await fetchVendorData();
        if (data) {
          setRowData(data);
        }
      } catch (error) {
        console.error('Error fetching vendor data:', error);
      }
    };
    getVendors();
  }, []);

  if (selectedVendor) {
    return (
      <ManageVendor
        vendor={selectedVendor}
        onBack={() => {
          setLoadingGrid(true);
          setSelectedVendor(null);
          setTimeout(() => {
            setLoadingGrid(false);
          }, 100);
        }}
      />
    );
  }

  const handleOpenDialog = () => {
    setErrorMessage('');
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setNewVendor({ vendorName: '', confirmVendorName: '' });
    setErrorMessage('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...newVendor, [name]: value };
    setNewVendor(updated);

    if (updated.vendorName && updated.confirmVendorName && updated.vendorName !== updated.confirmVendorName) {
      setErrorMessage('Vendor names do not match!');
    } else {
      setErrorMessage('');
    }
  };

  const handleAddVendor = async () => {
    if (newVendor.vendorName !== newVendor.confirmVendorName) {
      setErrorMessage('Vendor names do not match!');
      return;
    }

    const result = await addVendor(newVendor.vendorName);

    if (result.success) {
      toast.success('Vendor added successfully!');
      handleCloseDialog();
      const updatedVendors = await fetchVendorData();
      setRowData(updatedVendors);
    } else {
      setErrorMessage(result.message);
    }
  };

  const isFormValid =
    newVendor.vendorName && newVendor.confirmVendorName && newVendor.vendorName === newVendor.confirmVendorName;

  return (
    <div>
      {loadingFetchVendorData || loadingGrid ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
          }}
        >
          <CircularProgress />
          <Typography variant="h6" sx={{ marginTop: 2 }}>
            Loading Vendors...
          </Typography>
        </Box>
      ) : errorFetchVendorData ? (
        <Box sx={{ textAlign: 'center', color: 'red', marginTop: 2 }}>
          <Typography variant="h6">Error: {errorFetchVendorData.message}</Typography>
        </Box>
      ) : rowData.length === 0 ? (
        <Box sx={{ textAlign: 'center', marginTop: 2 }}>
          <Typography variant="h6">No vendors available.</Typography>
        </Box>
      ) : (
        <div style={{ height: 'calc(90)', padding: 3, paddingLeft: '20px' }}>
          <div>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
                mt: 1,
                pr: 2,
              }}
            >
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant="contained" color="primary" onClick={handleOpenDialog}>
                  Add New Vendor
                </Button>
                <Button variant="contained" color="secondary">
                  Clear Filters
                </Button>
              </Box>

              <Tooltip title="Watch Tutorial">
                <IconButton
                  onClick={() => window.open(`/tutorial?videoUrl=${encodeURIComponent(videoUrl)}`, '_blank')}
                  color="primary"
                >
                  {' '}
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </div>
          <div style={{ height: 'calc(85vh)' }}>
            <AgGridReact
              columnDefs={columnDefs}
              rowData={rowData}
              defaultColDef={{
                sortable: true,
                resizable: true,
                cellStyle: { textAlign: 'left' },
              }}
              animateRows={true}
              enableCellTextSelection={true}
              getRowStyle={getRowStyle}
              domLayout="normal"
              rowHeight={35}
            />
          </div>
        </div>
      )}

      <Dialog open={open} onClose={handleCloseDialog} fullWidth maxWidth="md">
        <DialogContent>
          <TextField
            label="Vendor Name"
            fullWidth
            margin="dense"
            name="vendorName"
            value={newVendor.vendorName}
            onChange={handleInputChange}
            required
          />
          <TextField
            label="Confirm Vendor Name"
            fullWidth
            margin="dense"
            name="confirmVendorName"
            value={newVendor.confirmVendorName}
            onChange={handleInputChange}
            required
            error={!!errorMessage}
            helperText={errorMessage}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddVendor}
            disabled={!isFormValid || loadingAddVendor}
          >
            Add Vendor
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default VendorMgmt;
